import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { readDraft } from "@/hooks/useFormDraftCapture";

const DISMISS_KEY = "mfi_resume_banner_dismissed";

interface DraftSnapshot {
  name?: string;
  phone?: string;
  email?: string;
  source?: string;
  page_path?: string;
  last_field?: string;
}

function isViable(d: DraftSnapshot): boolean {
  if (!d.name || d.name.trim().length < 2) return false;
  return Boolean(
    (d.phone && d.phone.replace(/\D/g, "").length >= 7) ||
      (d.email && /.+@.+\..+/.test(d.email)),
  );
}

function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Soft top-of-page banner shown when we detect a viable abandoned draft for
 * the current visit (returning user mid-quote, no `?resume=1` param needed).
 * Tapping the CTA navigates to the original form's page with `?resume=1` so
 * `useDraftResume` prefills + focuses the matching form.
 */
const ResumeQuoteBanner = () => {
  const [draft, setDraft] = useState<DraftSnapshot | null>(null);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't show if we're already in the resume flow
    if (new URLSearchParams(search).get("resume") === "1") return;
    if (isDismissed()) return;

    // Don't show on admin or auth-ish pages
    if (pathname.startsWith("/admin") || pathname.startsWith("/unsubscribe")) return;

    const d = readDraft() as DraftSnapshot;
    if (!isViable(d)) return;
    setDraft(d);

    void import("@/lib/analytics").then(({ conversions }) =>
      conversions.resumeBannerShown(d.source),
    );
  }, [pathname, search]);

  if (!draft) return null;

  const firstName = draft.name?.trim().split(/\s+/)[0];
  const basePath = draft.page_path && draft.page_path !== "/" ? draft.page_path : pathname;
  const params = new URLSearchParams({ resume: "1" });
  if (draft.source) params.set("src", draft.source);
  if (draft.last_field) params.set("field", draft.last_field);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    void import("@/lib/analytics").then(({ conversions }) =>
      conversions.resumeBannerClick(draft.source),
    );
    markDismissed();
    setDraft(null);
    const target = `${basePath}?${params.toString()}`;
    if (basePath === pathname) {
      // same page: still trigger by replacing search
      navigate(target, { replace: true });
    } else {
      navigate(target);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setDraft(null);
  };

  return (
    <div
      className="sticky top-16 z-40 bg-primary/10 border-b border-primary/20 backdrop-blur-xs"
      role="region"
      aria-label="Resume your quote"
    >
      <div className="container mx-auto px-3 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
        </div>
        <p className="flex-1 min-w-0 text-xs sm:text-sm text-foreground">
          <span className="font-bold">
            {firstName ? `Welcome back, ${firstName}.` : "Welcome back."}
          </span>{" "}
          <span className="text-muted-foreground">
            Pick up your quote where you left off — takes 30 seconds.
          </span>
        </p>
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground font-semibold text-xs sm:text-sm px-3 sm:px-4 h-8 sm:h-9 hover:bg-primary/90 transition-colors flex-shrink-0"
        >
          Resume <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1 text-muted-foreground hover:text-foreground rounded-md hover:bg-background/50 transition-colors flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ResumeQuoteBanner;
